import { CorvalCostOccurrenceFee, CorvalProjectBudgetItem, CorvalProjectBudgetItemValue } from "@src/types";
import { EnhancedCorvalCostOccurrenceFee }                                                from "@src/views/pages/projects/resource-planning/budgeting/by-category/BudgetVisualizationByCategory";


const enum ItemDataTypeNames {
    PROJECT_ACTIVITY           = 'ProjectActivity',
    PROJECT_BUDGET_ITEM        = 'ProjectBudgetItem',
    STATIC_PROJECT_ACTIVITY    = 'StaticProjectActivity',
    STATIC_PROJECT_BUDGET_ITEM = 'StaticProjectBudgetItem',
}

export class EnhancedCostOccurrenceHelper {

    static build( _costOccurrenceData: CorvalCostOccurrenceFee ){
        return {
            ..._costOccurrenceData,
            itemData:         new CostOccurrenceItemDataModel( _costOccurrenceData.itemData ),
            originalItemData: {
                ..._costOccurrenceData.itemData
            }
        }
    }

    static clone( _costOccurrence: EnhancedCorvalCostOccurrenceFee ){
        return {
            ..._costOccurrence,
            itemData:         new CostOccurrenceItemDataModel( _costOccurrence.itemData.data ),
            originalItemData: {
                ..._costOccurrence.originalItemData
            }
        }
    }
}

export class CostOccurrenceItemDataModel {

    private _data: any = {};
    private _type: ItemDataTypeNames = null;

    constructor( data, type: ItemDataTypeNames = ItemDataTypeNames.PROJECT_ACTIVITY ){

        this._data = data;
        this._type = data?.__typename ?? type;
    }

    get id(){ return this.getId() }

    get data(){ return this._data };

    get type(){ return this._type };

    isType( type: ItemDataTypeNames ){ return this._type === type };

    isProjectActivity(){
        return this.isType( ItemDataTypeNames.PROJECT_ACTIVITY )
    };

    isProjectBudgetItem(){
        return this.isType( ItemDataTypeNames.PROJECT_BUDGET_ITEM )
    };

    isStaticProjectActivity(){
        return this.isType( ItemDataTypeNames.STATIC_PROJECT_ACTIVITY )
    };

    isStaticProjectBudgetItem(){
        return this.isType( ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM )
    };

    get( field: string ){

        switch( field ){

            case 'name':
                return this.getBusinessFunction()
            case 'businessFunction':
                return this.getName()
        }

        return null
    };

    getId(){

        const defaultValue = null;

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return this._data?.id ?? defaultValue;
        }
    }

    getStartTime(){

        const defaultValue = null;

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
                return this._data?.startTimeUtc ?? defaultValue;

            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return this._data?.startDate ?? defaultValue;
        }
    }

    getName(){

        const defaultValue = '(Budget Item)';

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
                return this._data?.activity?.displayName ?? defaultValue;

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return this._data?.name ?? defaultValue;
        }
    }

    getActivityId(){

        const defaultValue = null;

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
                return this._data?.activity?.id ?? defaultValue;
            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return defaultValue;
        }
    }

    getBusinessFunction(){

        const defaultValue = null;

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
                return this._data?.activity?.businessFunction ?? defaultValue;

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return this._data?.businessFunction ?? defaultValue;
        }
    }

    getRequiredHires(){

        const defaultValue = [];

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
                return this._data?.requiredHires ?? defaultValue;

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return defaultValue;
        }
    }

    getCategoryId(){

        const defaultValue = null;

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return this._data?.getCategoryId ?? defaultValue;
        }
    }

    getSubObjectiveId(){

        const defaultValue = null;

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
                return this._data?.subObjectiveId ?? defaultValue;

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return defaultValue;
        }
    }

    getTags(){

        const defaultValue = null;

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
                return this._data?.activity.tags ?? defaultValue;

            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return defaultValue;
        }
    }

    getCost( type:
                 'ongoing'
                 | 'operational'
			     | 'scheduled'
                 | 'operationalFeeReplacement'
                 | 'defaultOperational'
                 | 'defaultOngoingCostMin'
                 | 'defaultOngoingCostMax'
    ){

        const defaultValue = null;

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:

                switch( type ){
                    case 'operational':
                        return this._data?.operationalCost ?? defaultValue;
                    case 'defaultOperational':
                        return this._data?.activity.defaultOperationalCost ?? defaultValue;
                    case 'ongoing':
                        return this._data?.ongoingCost ?? defaultValue;
                    case 'defaultOngoingCostMin':
                        return this._data?.activity.defaultOngoingCostMin ?? defaultValue;
                    case 'defaultOngoingCostMax':
                        return this._data?.activity.defaultOngoingCostMax ?? defaultValue;
					case 'scheduled':
						return this._data?.scheduledCost ?? defaultValue;
                }

            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:

                switch( type ){
                    case 'operational':
                    case 'defaultOperational':
                        return this._data?.operationalCost ?? defaultValue;
					case 'scheduled':
                    case 'ongoing':
                    case 'defaultOngoingCostMin':
                    case 'defaultOngoingCostMax':
                        return this._data?.ongoingCost ?? defaultValue;
                }

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return defaultValue;
        }
    }

    getCostBehavior( type: 'operationalFeeReplacement' ){

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:

                switch( type ){
                    case 'operationalFeeReplacement':
                        return false;
                }

            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:

                switch( type ){
                    case "operationalFeeReplacement":
                        return this._data?.operationalCostReplacesProfessionalFees ?? false;
                }
        }
    }

    setCost( type:
                 'ongoing'
                 | 'operational'
				 | 'scheduled'
                 | 'defaultOperational'
                 | 'defaultOngoingCostMin'
                 | 'defaultOngoingCostMax', value: any = 0 ){

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:

                switch( type ){
                    case 'ongoing':
                        this._data.ongoingCost = value;
                        break;
                    case 'operational':
                        this._data.operationalCost = value;
                        break;
                    case 'defaultOperational':
                        this._data.activity.defaultOperationalCost = value;
                        break;
                    case 'defaultOngoingCostMin':
                        this._data.activity.defaultOngoingCostMin = value;
                        break;
                    case 'defaultOngoingCostMax':
                        this._data.activity.defaultOngoingCostMax = value;
                        break;
                }

            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:

                switch( type ){
                    case 'ongoing':
                    case 'defaultOngoingCostMin':
                    case 'defaultOngoingCostMax':
                        this._data.ongoingCost = value;
                        break;
                    case 'operational':
                    case 'defaultOperational':
                        this._data.operationalCost = value;
                        break;
					case 'scheduled':
						this._data.scheduledCost = value;
						break;
                }

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                break;
        }
    }

    getCostNoteInfo( type: 'ongoing' | 'operational' | 'scheduled' ){

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:

                switch( type ){
                    case 'ongoing':
                        return this._data.ongoingCostNoteOverride;
                    case 'operational':
                        return this._data.operationalCostNoteOverride;
                }

            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:

                switch( type ){
                    case 'ongoing':
                        return this._data.ongoingCostNote;
                    case 'operational':
                        return this._data.operationalCostNote;
					case 'scheduled':
						return this._data.scheduledCostNote;
                }

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return null
        }
    }

    getProfessionalFees(){

        const defaultValue = [];

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
                return this._data?.professionalFees ?? defaultValue;

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return defaultValue;
        }
    }

    getScheduledCosts(){

        const defaultValue = [];

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
                return this._data?.scheduledBudgetItems ?? defaultValue;

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return defaultValue;
        }
    }

    getCustomValues(){

        const defaultValue = [];

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
                return defaultValue;

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                return this._data?.values ?? defaultValue;
        }
    }

    setCustomValues( valueFunc: ( value: CorvalProjectBudgetItemValue ) => number ){

        const defaultValue = [];

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:
            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
                break;

            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:
                if( this._data?.values ){
                    this._data.values = ( this._data?.values as CorvalProjectBudgetItem['values'] ).map( item => {
                        return valueFunc( item );
                    } );
                }
                break;
        }
    }

    getRecurrenceInfo( type:
                           'isRecurrence'
                           | 'recurrenceSourceProjectActivityId'
                           | 'recurrenceType'
    ){

        switch( this._type ){
            case ItemDataTypeNames.PROJECT_ACTIVITY:

                switch( type ){
                    case 'isRecurrence':
                        return this._data.isRecurrence;
                    case 'recurrenceSourceProjectActivityId':
                        return this._data.recurrenceSourceProjectActivityId;
                    case 'recurrenceType':
                        return this._data.recurrenceType;
                }

            case ItemDataTypeNames.STATIC_PROJECT_ACTIVITY:
            case ItemDataTypeNames.PROJECT_BUDGET_ITEM:
            case ItemDataTypeNames.STATIC_PROJECT_BUDGET_ITEM:

                switch( type ){
                    case 'isRecurrence':
                        return false;
                    case 'recurrenceSourceProjectActivityId':
                    case 'recurrenceType':
                        return null;
                }
        }
    }
}
